/**
 * The door between the two halves of the game.
 *
 * A campaign is played in two places — the realm, where hosts march and
 * provinces change hands, and your capital, where the actual town is. This is
 * the control that moves the player between them, and the reason it is its own
 * file rather than another panel in the HUD is that it has to be visible in
 * *both* places and outlive the world being swapped underneath it.
 *
 * It also carries the one piece of strategic information the city genuinely
 * needs: whether anything is marching at you, and how long you have. A player
 * laying out a new quarter should be able to see, without leaving, that there
 * are eleven hundred Ironfang three provinces out and closing.
 *
 * Styles are injected under their own id rather than added to `style.ts`, so
 * this panel can be added, moved or deleted without touching the stylesheet
 * everything else shares.
 */

import { setHtml } from './dom.ts';
import { toCss, C } from '../data/palette.ts';
import { nationAccent, nationDef } from '../data/nations.ts';
import { provinceDef } from '../data/provinces.ts';
import type { World } from '../sim/world.ts';
import { armyPoints } from '../sim/campaign/armies.ts';
import { neighbours } from '../sim/campaign/graph.ts';
import { settledHoldings } from '../sim/campaign/holdworld.ts';
import { crownPool } from '../sim/campaign/commonwealth.ts';
import { isVillageHolding, villageLevel, villageReport } from '../sim/campaign/villages.ts';
import { formatDateShort } from '../sim/systems/calendar.ts';

export interface TravelCallbacks {
  /** Leave the city and open the realm. */
  onToRealm(): void;
  /**
   * Go and stand in another town of yours, by province index.
   *
   * *"you can swap between towns once you capture to build both"*. The swap
   * lives here rather than on the world map because this is the panel that is
   * on screen in **both** halves of the game and outlives the world being
   * swapped underneath it — which is the reason this file exists at all, and it
   * is the same reason twice.
   */
  onEnterHold(province: number): void;
}

/** A host that is close enough to the capital to be worth worrying about. */
interface Threat {
  nation: number;
  points: number;
  /** Provinces away: 0 = it is here, 1 = next door. */
  distance: number;
  turns: number;
}

export class Travel {
  private el: HTMLElement;
  /**
   * The Towns book, down the right-hand column.
   *
   * > *"in the city view, make it so on the right side there is a new category
   * > where you can quick switch between town views"*
   *
   * The bar at the top already carries a swap list behind a caret, and it is
   * kept: it is the control you reach for when you already know where you are
   * going. What it is not is a *category* — a standing panel that says how many
   * towns the crown has, what each of them is, and what is in their barns
   * without being opened first. A menu you have to open to learn that your
   * second village has been starving for a month is a menu that has told you
   * nothing.
   *
   * It lives in this file rather than in `hud.ts` for the reason the rest of
   * this panel does: it is about the *crown*, it outlives the world being
   * swapped underneath it, and the verb it needs — `onEnterHold` — is already
   * here. The HUD would have had to be handed the campaign state and the town
   * roster to draw it, which is two new dependencies for a panel that is not
   * about the settlement on screen.
   *
   * A separate element from `#travel` rather than a child of it, because the
   * two are pinned to different corners and a flex row cannot be in both.
   */
  private book: HTMLElement;
  private visible = false;
  /**
   * Whether the list of the crown's towns is down.
   *
   * Held here and written into the markup as a class, so `setHtml`'s "only
   * write when the words changed" comparison sees the open and closed panels as
   * two different panels and redraws once on each transition. A flag kept out
   * of the markup would be a flag the panel never noticed.
   */
  private listOpen = false;

  /** The count stays visible; the holding list opens when it is needed. */
  private bookOpen = false;

  constructor(private cb: TravelCallbacks) {
    injectTravelStyle();
    const root = document.getElementById('ui-root') as HTMLElement;
    this.el = document.createElement('div');
    this.el.id = 'travel';
    this.el.style.display = 'none';
    root.appendChild(this.el);
    this.book = document.createElement('div');
    this.book.id = 'townbook';
    this.book.className = 'panel';
    this.book.style.display = 'none';
    root.appendChild(this.book);
  }

  show(): void {
    this.visible = true;
    this.el.style.display = '';
  }

  hide(): void {
    this.visible = false;
    this.el.style.display = 'none';
    this.showBook(false);
  }

  /**
   * Put the book away, and give the roster its column back.
   *
   * The custom property is read by `#roster` in `style.ts` and is the only
   * coupling between the two panels. A class would have needed a height in it,
   * and the book's height is however many towns the crown has.
   *
   * **`measure` is the whole reason this takes an argument.** `offsetHeight` is
   * a forced synchronous layout: the browser has to flush every pending style
   * change in the document to answer it. `drawBook` runs sixty times a second,
   * so reading it unconditionally would make the one panel on screen that
   * almost never changes the most expensive thing in the frame. It is read only
   * when the markup was actually rewritten, which `setHtml` already reports —
   * and on the frame the panel first appears.
   */
  private bookShown = false;

  private showBook(on: boolean, measure = false): void {
    const changed = on !== this.bookShown;
    if (!changed && !measure) return;
    this.bookShown = on;
    if (changed) this.book.style.display = on ? '' : 'none';
    const root = document.getElementById('ui-root');
    if (!root) return;
    root.style.setProperty('--towns-top', on ? `${this.book.offsetHeight + 44}px` : '40px');
  }

  get isOpen(): boolean {
    return this.visible;
  }

  /**
   * Redraw. Called every frame with whichever world is on screen; a world with
   * no campaign attached (a skirmish, a standalone battle) hides the panel, so
   * the door only exists where it leads somewhere.
   */
  update(city: World, campaign: World | null, towns?: ReadonlyMap<number, World>): void {
    if (!this.visible) return;
    const c = campaign?.campaign;
    if (!c) {
      setHtml(this.el, '');
      this.showBook(false);
      return;
    }

    /**
     * Where the player is standing, which is no longer the same question as
     * "where is your capital".
     *
     * This line read `c.capital` and printed the seat's name on the panel
     * whatever town you were in — so a player standing in a captured hamlet was
     * told, in the one place on screen that says where you are, that they were
     * at home. `config.province` is written by `holdConfigFor` and absent on
     * every world made before a crown could hold two, which is exactly the set
     * of worlds that *are* the capital.
     */
    const here = city.config.province ?? c.capital;
    const prov = provinceDef(here);
    const nation = nationDef(c.nations[c.playerNation].def);
    const accent = toCss(nationAccent(c.nations[c.playerNation].def));

    const threats = threatsToCapital(campaign as World);
    const worst = threats[0];

    const threatHtml = worst
      ? `<div class="tv-threat ${worst.distance === 0 ? 'here' : ''}">
           <b>${esc(nationDef(c.nations[worst.nation].def).name)}</b>
           ${worst.distance === 0
             ? 'is at the gates.'
             : `is ${worst.distance === 1 ? 'next door' : `${worst.distance} provinces out`} — ` +
               `about ${worst.turns} month${worst.turns === 1 ? '' : 's'}.`}
           <span class="tv-pts">${Math.round(worst.points)} pts</span>
         </div>`
      : `<div class="tv-threat quiet">Nothing on the roads. For now.</div>`;

    /**
     * Through `setHtml`, because the last element in here is a *button*.
     *
     * This ran every frame and replaced the whole bar every frame, which meant
     * "To the Realm" was a fresh element by the time any human finished pressing
     * it — and a `click` needs the press and the release to land on the same
     * node. The one panel whose whole job is to be the door between the two
     * halves of the game was a door that only opened for the keyboard. The
     * contents change once a simulated day, when the date does.
     */
    /**
     * The crown's other towns, and the list that swaps between them.
     *
     * Every holding the player has stood in is a real settlement world that has
     * been running the whole time; this is how you get back to one. Only drawn
     * when there is more than one — a player with a single town does not need a
     * menu offering it to them, and an affordance that never does anything is
     * worse than no affordance.
     *
     * The seat is marked rather than sorted to the top: the list is in province
     * order, which is the order the world map is in, so a player who knows
     * where their towns are on the chart can find them here.
     */
    const held = settledHoldings(c);
    const many = held.length > 1;
    const listHtml =
      many && this.listOpen
        ? `<div class="tv-list">${held
            .map((q) => {
              const wrecked = c.provinces[q].wreck > 0.005;
              return (
                `<div class="tv-town${q === here ? ' on' : ''}" data-prov="${q}">` +
                `<span class="tv-tname">${esc(provinceDef(q).name)}</span>` +
                `<span class="tv-tnote">${
                  q === c.capital ? 'the seat' : wrecked ? 'wrecked' : rankNote(c, q)
                }</span></div>`
              );
            })
            .join('')}</div>`
        : '';

    setHtml(
      this.el,
      `<div class="tv-where${many ? ' pick' : ''}${this.listOpen ? ' open' : ''}" id="tv-where">
        <span class="tv-dot" style="background:${accent}"></span>
        <div>
          <div class="tv-city">${esc(prov.name)}${many ? '<span class="tv-caret">▾</span>' : ''}</div>
          <div class="tv-nation">${esc(nation.name)} · ${esc(formatDateShort(campaign))}${
            many ? ` · ${held.length} towns` : ''
          }</div>
        </div>
        ${listHtml}
      </div>
      ${threatHtml}
      <div class="tv-go" id="tv-go">To the Realm <span class="tv-key">Tab</span></div>`,
    );

    const go = this.el.querySelector('#tv-go') as HTMLElement | null;
    if (go) go.onclick = () => this.cb.onToRealm();
    const where = this.el.querySelector('#tv-where') as HTMLElement | null;
    if (where && many) {
      where.onclick = (ev) => {
        const row = (ev.target as HTMLElement | null)?.closest('.tv-town') as HTMLElement | null;
        if (row) {
          this.listOpen = false;
          const q = Number(row.dataset.prov);
          if (q !== here) this.cb.onEnterHold(q);
          return;
        }
        this.listOpen = !this.listOpen;
      };
    }

    this.drawBook(c, held, here, towns);
  }

  /**
   * The Towns book: every settlement the crown runs, and one click to stand in
   * it.
   *
   * Drawn from two sources and it matters which says what. The *province* knows
   * the name, the rung and whether the place was sacked — that is true of every
   * holding, entered or not. The *world*, when there is one, knows how many
   * people are actually in it, what is in the barn and what is half-built, and
   * a holding nobody has walked into has none of that yet. So a town the player
   * has never entered still gets a row, marked as unvisited: it is a door, and
   * a door you have not opened is the one you most need shown.
   *
   * One panel, redrawn through `setHtml`, and the click handler is hung on the
   * container rather than on the rows — the rows are replaced whenever a number
   * on them changes, and a listener on a node that is about to be thrown away is
   * the swallowed-click bug this file already carries a paragraph about.
   */
  private drawBook(
    c: NonNullable<World['campaign']>,
    held: readonly number[],
    here: number,
    towns?: ReadonlyMap<number, World>,
  ): void {
    /**
     * Every holding of the crown's, not only the settled ones.
     *
     * `settledHoldings` is the list the swap caret offers, because you can only
     * swap to a town that exists. This panel is a *census*, so it counts the
     * ground as well: a crown with one town and four unentered holdings has
     * five places to build and the caret would show it one.
     */
    const all: number[] = [];
    for (let p = 0; p < c.provinces.length; p++) {
      if (c.provinces[p].owner === c.playerNation) all.push(p);
    }
    if (all.length < 2) {
      this.showBook(false);
      return;
    }

    const pool = crownPool(towns ? [...towns.values()] : []);
    const rows = all
      .map((q) => {
        const prov = c.provinces[q];
        const w = towns?.get(q) ?? null;
        const settled = held.includes(q);
        const kind = q === c.capital ? 'seat' : isVillageHolding(c, q) ? 'village' : 'city';
        const wrecked = prov.wreck > 0.005;
        let note: string;
        if (kind === 'village') {
          const report = villageReport(c, q);
          note = `${report?.profile.name ?? 'Resource village'} · level ${villageLevel(c, q)}/7`;
        } else if (!w) note = settled ? 'standing' : 'not yet entered';
        else {
          const raising = sitesRaising(w);
          note =
            `${Math.round(w.pop)} souls` +
            (raising > 0 ? ` · ${raising} rising` : '') +
            (wrecked ? ' · wrecked' : '');
        }
        return (
          `<div class="tb-row${q === here ? ' on' : ''}${w ? '' : ' cold'}" data-prov="${q}">` +
          `<span class="tb-kind ${kind}"></span>` +
          `<span class="tb-body">` +
          `<span class="tb-name">${esc(provinceDef(q).name)}</span>` +
          `<span class="tb-note">${esc(note)}</span>` +
          `</span>` +
          `<span class="tb-rank">${esc(kind === 'seat' ? 'seat' : kind)}</span>` +
          `</div>`
        );
      })
      .join('');

    /**
     * What the crown has, everywhere, in the four materials a build actually
     * spends. The pool is the point of the panel as much as the rows are: a
     * town borrows from its neighbours now (see `commonwealth.ts`), so "what
     * can I afford" stopped being a question about the strip at the top of the
     * screen the day the second town was founded.
     */
    const purse = BOOK_KEYS.map(
      (k) =>
        `<span class="tb-p"><i class="tb-${k}"></i>${Math.floor(pool[k]).toLocaleString('en')}</span>`,
    ).join('');

    const wrote = setHtml(
      this.book,
      `<button type="button" class="hdr tb-head" id="tb-head" aria-expanded="${this.bookOpen}" aria-controls="townbook-list">The Crown's Towns<span class="tb-n">${all.length}</span>` +
        `<span class="tb-caret">${this.bookOpen ? '▾' : '▸'}</span></button>` +
        (this.bookOpen ? `<div class="tb-list" id="townbook-list">${rows}</div><div class="tb-purse">${purse}</div>` : ''),
    );

    if (wrote) {
      const head = this.book.querySelector('#tb-head') as HTMLElement | null;
      if (head) {
        head.onclick = () => {
          this.bookOpen = !this.bookOpen;
        };
      }
      const list = this.book.querySelector('.tb-list') as HTMLElement | null;
      if (list) {
        list.onclick = (ev) => {
          const row = (ev.target as HTMLElement | null)?.closest('.tb-row') as HTMLElement | null;
          if (!row) return;
          const q = Number(row.dataset.prov);
          if (q !== here) this.cb.onEnterHold(q);
        };
      }
    }
    // `wrote` is the layout read's licence: the panel's height can only have
    // moved if its contents did.
    this.showBook(true, wrote);
  }

  /** Close the town list — for a world swap, which invalidates every row in it. */
  closeList(): void {
    this.listOpen = false;
  }
}

/** A one-word reading of what this holding is, for the swap list. */
function rankNote(c: NonNullable<World['campaign']>, p: number): string {
  const t = c.provinces[p].tier;
  if (t >= 8) return 'a great city';
  if (t >= 6) return 'a city';
  if (t >= 4) return 'a town';
  return 'a village';
}

/**
 * The four the book prints. Not every resource — there are two dozen and this
 * is a column 268 pixels wide — but the four that a foundation is priced in,
 * which is the question the panel is being asked.
 */
const BOOK_KEYS = ['timber', 'stone', 'iron', 'grain'] as const;

/** How many sites in this town are still going up. */
function sitesRaising(w: World): number {
  const b = w.buildings;
  let n = 0;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.build[i] < 1) n++;
  }
  return n;
}

/**
 * Everything hostile within a few provinces of the capital, worst first.
 *
 * A breadth-first walk rather than a straight-line distance, because the roads
 * are what an army actually has to march down — a host two provinces away
 * across a river is further away than the map looks.
 */
export function threatsToCapital(campaign: World, maxDepth = 3): Threat[] {
  const c = campaign.campaign;
  if (!c) return [];
  const capital = c.capital;

  const depth = new Map<number, number>([[capital, 0]]);
  const queue = [capital];
  for (let head = 0; head < queue.length; head++) {
    const p = queue[head];
    const d = depth.get(p) ?? 0;
    if (d >= maxDepth) continue;
    for (const n of neighbours(c, p)) {
      if (depth.has(n)) continue;
      depth.set(n, d + 1);
      queue.push(n);
    }
  }

  const out: Threat[] = [];
  for (const army of c.armies) {
    if (army.owner === c.playerNation) continue;
    // Where it will be, not where it is: an army mid-march is already committed.
    const at = army.moveTo >= 0 ? army.moveTo : army.at;
    const d = depth.get(at);
    if (d === undefined) continue;
    if (!(c.nations[army.owner]?.atWar[c.playerNation] === 1)) continue;
    out.push({
      nation: army.owner,
      points: armyPoints(army),
      distance: d,
      // One turn per province of road, plus whatever is left of its current leg.
      turns: Math.max(1, d + (army.moveTo >= 0 ? army.moveT : 0)),
    });
  }
  // Closest first, then heaviest — the thing arriving soonest is the thing that
  // decides what you build this turn.
  out.sort((a, b) => a.distance - b.distance || b.points - a.points);
  return out;
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch] as string);
}

let styled = false;
function injectTravelStyle(): void {
  if (styled) return;
  styled = true;
  const s = document.createElement('style');
  s.id = 'travel-style';
  s.textContent = `
    /**
     * Below the resource strip, and inset past the Works panel on the left and
     * the Companies panel on the right. Sat at the very top first, where it
     * covered the resource strip and half the scouting report — the HUD owns
     * that band and this is a guest in it.
     */
    #travel { position: fixed; top: 46px; right: 288px;
      z-index: 40; display: flex; align-items: stretch; gap: 0;
      font-family: Inter, system-ui, sans-serif; pointer-events: none; }
    #travel > * { pointer-events: auto; }
    #travel .tv-where, #travel .tv-threat, #travel .tv-go {
      background: rgba(16,15,13,0.9); border: 1px solid rgba(217,207,188,0.16);
      padding: 8px 14px; display: flex; align-items: center; gap: 10px; }
    #travel .tv-where { border-radius: 3px 0 0 3px; border-right: none; }
    #travel .tv-threat { border-right: none; font-size: 12px; color: ${toCss(C.boneDim)}; max-width: 340px; }
    #travel .tv-threat b { color: ${toCss(C.bone)}; font-weight: 500; }
    #travel .tv-threat.here { color: ${toCss(C.oxbloodLight)}; }
    #travel .tv-threat.here b { color: ${toCss(C.oxbloodLight)}; }
    #travel .tv-threat.quiet { color: ${toCss(C.boneDeep)}; font-style: italic; }
    #travel .tv-pts { color: ${toCss(C.boneDeep)}; font-variant-numeric: tabular-nums; margin-left: 4px; }
    #travel .tv-dot { width: 9px; height: 9px; border-radius: 50%; flex: none;
      box-shadow: 0 0 0 1px rgba(0,0,0,0.6); }
    #travel .tv-city { font-family: Cinzel, Georgia, serif; font-size: 14px;
      letter-spacing: 0.1em; color: ${toCss(C.bone)}; }
    #travel .tv-nation { font-size: 10.5px; letter-spacing: 0.16em; text-transform: uppercase;
      color: ${toCss(C.boneDeep)}; margin-top: 2px; }
    /* The town switcher. The name block is the positioning anchor, not the bar:
       the bar is a flex row, and a list hung off it would stretch the whole
       width of it. (No backticks in here — this block is a template literal.) */
    #travel .tv-where { position: relative; }
    #travel .tv-where.pick { cursor: pointer; }
    #travel .tv-where.pick:hover .tv-city { color: ${toCss(C.goldPale)}; }
    #travel .tv-caret { font-size: 9px; margin-left: 6px; vertical-align: 2px;
      color: ${toCss(C.boneDeep)}; }
    #travel .tv-where.open .tv-caret { color: ${toCss(C.gold)}; }
    #travel .tv-list { position: absolute; top: 100%; left: -1px; min-width: calc(100% + 2px);
      margin-top: 2px; background: rgba(16,15,13,0.97);
      border: 1px solid rgba(217,207,188,0.22); border-radius: 3px;
      box-shadow: 0 8px 22px rgba(0,0,0,0.6); z-index: 2; overflow: hidden;
      max-height: 320px; overflow-y: auto; }
    #travel .tv-town { display: flex; align-items: baseline; justify-content: space-between;
      gap: 16px; padding: 7px 12px; white-space: nowrap;
      border-bottom: 1px solid rgba(217,207,188,0.07); }
    #travel .tv-town:last-child { border-bottom: none; }
    #travel .tv-town:hover { background: rgba(200,164,76,0.16); }
    #travel .tv-town.on { background: rgba(200,164,76,0.10); }
    #travel .tv-tname { font-family: Cinzel, Georgia, serif; font-size: 12.5px;
      letter-spacing: 0.06em; color: ${toCss(C.bone)}; }
    #travel .tv-town.on .tv-tname { color: ${toCss(C.goldPale)}; }
    #travel .tv-tnote { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
      color: ${toCss(C.boneDeep)}; }
    #travel .tv-go { border-radius: 0 3px 3px 0; cursor: pointer;
      font-size: 11.5px; letter-spacing: 0.18em; text-transform: uppercase;
      color: ${toCss(C.gold)}; transition: background 120ms, color 120ms; }
    #travel .tv-go:hover { background: rgba(200,164,76,0.16); color: ${toCss(C.goldPale)}; }
    #travel .tv-key { font-size: 9.5px; letter-spacing: 0.1em; color: ${toCss(C.boneDeep)};
      border: 1px solid rgba(217,207,188,0.22); border-radius: 2px; padding: 1px 5px; }

    /* ── The Towns book ───────────────────────────────────────
       Top of the right-hand column, above the roster — which is pushed down by
       exactly this panel's height through --towns-top rather than by a magic
       number, because the height is however many holdings the crown has.
       268px is the column: the roster and the chart are both that wide and a
       third width down the same edge would read as a mistake. */
    /* Absolute and id-qualified, both for the same reason: #ui-root .panel is
       position:absolute at a higher specificity than a bare #townbook, so a
       'fixed' here would be silently overruled and the panel would look right
       only by accident. The roster below it is positioned exactly this way. */
    #ui-root #townbook { position: absolute; top: 40px; right: 0; width: 268px;
      z-index: 30; font-family: Inter, system-ui, sans-serif; }
    #townbook .tb-head { display: flex; align-items: center; gap: 7px; cursor: pointer;
      user-select: none; }
    #townbook .tb-head:hover { color: ${toCss(C.goldPale)}; }
    #townbook .tb-n { font-size: 10px; color: ${toCss(C.boneDeep)};
      font-variant-numeric: tabular-nums; border: 1px solid rgba(217,207,188,0.2);
      border-radius: 2px; padding: 0 4px; }
    #townbook .tb-caret { margin-left: auto; font-size: 9px; color: ${toCss(C.boneDeep)}; }
    #townbook .tb-list { max-height: 214px; overflow-y: auto; padding: 4px; }
    #townbook .tb-row { display: flex; align-items: center; gap: 7px; padding: 5px 7px;
      cursor: pointer; background: rgba(255,255,255,0.022); margin-bottom: 3px;
      border-left: 3px solid ${toCss(C.goldDeep)}; }
    #townbook .tb-row:hover { background: rgba(200,164,76,0.16); }
    #townbook .tb-row.on { background: rgba(200,164,76,0.16); border-left-color: ${toCss(C.goldPale)}; }
    /* A holding with no world behind it yet. Dimmed rather than hidden: it is
       still a door, and it is the one the player has not opened. */
    #townbook .tb-row.cold { border-left-color: rgba(217,207,188,0.18); }
    #townbook .tb-row.cold .tb-name { color: ${toCss(C.boneDim)}; }
    /* The rung, as a shape rather than a word: a village is a dot, a town a
       square, the seat a diamond. The word is on the right as well — this is
       268 pixels and the shape is what you actually read at a glance. */
    #townbook .tb-kind { flex: none; width: 8px; height: 8px; border-radius: 50%;
      background: ${toCss(C.boneDeep)}; }
    #townbook .tb-kind.town { border-radius: 1px; background: ${toCss(C.bone)}; }
    #townbook .tb-kind.city { border-radius: 1px; background: ${toCss(C.gold)}; }
    #townbook .tb-kind.seat { border-radius: 1px; background: ${toCss(C.goldPale)};
      transform: rotate(45deg); }
    #townbook .tb-body { flex: 1; min-width: 0; display: flex; flex-direction: column; }
    #townbook .tb-name { font-family: Cinzel, Georgia, serif; font-size: 12px;
      letter-spacing: 0.06em; color: ${toCss(C.bone)}; white-space: nowrap;
      overflow: hidden; text-overflow: ellipsis; }
    #townbook .tb-row.on .tb-name { color: ${toCss(C.goldPale)}; }
    #townbook .tb-note { font-size: 10px; color: ${toCss(C.boneDeep)};
      font-variant-numeric: tabular-nums; }
    #townbook .tb-rank { flex: none; font-size: 9px; letter-spacing: 0.13em;
      text-transform: uppercase; color: ${toCss(C.boneDeep)}; }
    /* The pooled stores. The whole point of the panel as much as the rows are:
       a build spends the crown's materials now, not this town's. */
    #townbook .tb-purse { display: flex; gap: 4px; justify-content: space-between;
      padding: 5px 8px; border-top: 1px solid rgba(217,207,188,0.1);
      font-size: 10.5px; color: ${toCss(C.boneDim)}; font-variant-numeric: tabular-nums; }
    #townbook .tb-p { display: flex; align-items: center; gap: 3px; }
    #townbook .tb-p i { width: 7px; height: 7px; display: inline-block; }
    #townbook .tb-timber { background: #7a5a32; }
    #townbook .tb-stone { background: #8a8577; }
    #townbook .tb-iron { background: #6d7480; }
    #townbook .tb-grain { background: #b39b46; }

    /* Narrow screens. This bar is pinned 288px from the right edge because that
       is where the roster column ends — and on a phone there is no roster
       column, so it hung off the left of the screen as a three-word stack. It
       becomes one strip across the top of the map instead, with the threat
       reading dropped: it is a sentence, and a sentence does not fit. */
    @media (max-width: 820px) {
      #travel { top: 48px; right: 4px; left: 4px; }
      #travel .tv-threat { display: none; }
      #travel .tv-where { flex: 1; min-width: 0; }
      #travel .tv-where, #travel .tv-go { padding: 7px 10px; }
      #travel .tv-city { font-size: 12px; white-space: nowrap; overflow: hidden;
        text-overflow: ellipsis; }
      #travel .tv-nation { display: none; }
      #travel .tv-key { display: none; }
      /* No right-hand column to sit at the top of. The book becomes a row of
         chips under the bar instead — the names and the swap survive, the
         census does not, because a census is a table. */
      #ui-root #townbook { position: absolute; top: 92px; right: 4px; left: 4px; width: auto; }
      #townbook .tb-list { display: flex; gap: 4px; overflow-x: auto; overflow-y: hidden;
        max-height: none; }
      #townbook .tb-row { margin-bottom: 0; }
      #townbook .tb-note, #townbook .tb-rank, #townbook .tb-purse { display: none; }
    }
  `;
  document.head.appendChild(s);
}
